import * as Yup from 'yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';

import { useRouter, useSearchParams } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';
import { paths } from 'src/routes/paths';

import { useBoolean } from 'src/hooks/use-boolean';

import { useAuthContext } from 'src/auth/hooks';
import { PATH_AFTER_LOGIN } from 'src/config-global';

import Iconify from 'src/components/iconify';
import FormProvider, { RHFCheckbox, RHFTextField } from 'src/components/hook-form';
import Logo from 'src/components/logo';

import { useTranslate } from 'src/locales';
import { LanguageSwitch } from 'src/layouts/dashboard/components/language-switch';
import { getSecureItem } from 'src/utils/crypto';

// เพิ่ม key สำหรับเก็บข้อมูล remember me
const REMEMBER_EMAIL_KEY = 'rememberEmail';
const REMEMBER_TOKEN_KEY = 'rememberToken';

// ----------------------------------------------------------------------

export default function JwtLoginView() {
  const { login } = useAuthContext();

  const { t: tField } = useTranslate('field');
  const { t: tAlert } = useTranslate('alert');

  const router = useRouter();

  const [errorMsg, setErrorMsg] = useState('');

  const searchParams = useSearchParams();

  const returnTo = searchParams.get('returnTo');

  const password = useBoolean();

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .required(`${tField('email')} ${tField('form.isRequired')}`)
      .email(`${tField('email')} must be a valid email address`),
    password: Yup.string().required(`${tField('password')} ${tField('form.isRequired')}`),
    remember: Yup.boolean(),
  });

  // ดึงข้อมูลที่จำไว้มาใส่ในฟอร์ม
  const rememberedEmail = getSecureItem(REMEMBER_EMAIL_KEY);
  const rememberedToken = getSecureItem(REMEMBER_TOKEN_KEY);

  const defaultValues = {
    email: rememberedEmail || '',
    password: rememberedToken || '', // แสดงรหัสผ่านที่จำไว้
    remember: !!rememberedEmail && !!rememberedToken, // ถ้ามีข้อมูลที่จำไว้ ให้เลือก remember โดยอัตโนมัติ
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await login?.(data.email, data.password, data.remember);
      router.push(returnTo || PATH_AFTER_LOGIN);
    } catch (error) {
      if (error.code === 'auth/invalid-credential') {
        setErrorMsg(tAlert('error.auth.invalidCredential'));
      } else if (error.code === 'auth/unauthorized') {
        setErrorMsg(tAlert('error.auth.unauthorized'));
      } else if (error.message === 'Permission denied') {
        setErrorMsg(tAlert('error.auth.permissionDenied'));
      } else if (error.status === 401) {
        setErrorMsg(tAlert('error.auth.permissionDenied'));
      } else {
        setErrorMsg(typeof error === 'string' ? error : error.message);
      }
    }
  });

  const renderForm = (
    <Stack spacing={2.5}>
      <RHFTextField
        autoComplete="new-password"
        name="email"
        placeholder={tField('login.emailPlaceholder')}
        label={tField('email')}
      />

      <RHFTextField
        name="password"
        label={tField('password')}
        autoComplete="new-password"
        placeholder={tField('login.passwordPlaceholder')}
        type={password.value ? 'text' : 'password'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={password.onToggle} edge="end">
                <Iconify icon={password.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <RHFCheckbox name="remember" label={tField('login.rememberMe')} />
        <Link
          component={RouterLink}
          href={paths.auth.forgotPassword}
          variant="body2"
          color="primary"
          underline="hover"
        >
          {tField('login.forgotPassword')}
        </Link>
      </Stack>

      <LoadingButton
        fullWidth
        color="inherit"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        {tField('login.loginButton')}
      </LoadingButton>
    </Stack>
  );

  return (
    <>
      <Box sx={{ position: 'absolute', right: 24, top: 24 }}>
        <LanguageSwitch />
      </Box>

      <Stack
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 5 }}
        spacing={2}
      >
        <Logo sx={{ width: 80, height: 80, '& svg': { width: 80, height: 80 } }} />
        <Typography variant="h4" textAlign="center" sx={{ mt: 1 }}>
          {tField('login.loginToSystem')}
        </Typography>
      </Stack>

      {!!errorMsg && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMsg}
        </Alert>
      )}

      <FormProvider methods={methods} onSubmit={onSubmit}>
        {renderForm}
      </FormProvider>
    </>
  );
}
